import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';

const TrialBalance = () => {
    const { token } = useAuth();
    const [filterType, setFilterType] = useState('date');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [monthValue, setMonthValue] = useState('');
    const [weekOptions, setWeekOptions] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ total_debit: 0, total_credit: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastRun, setLastRun] = useState(null);

    const filterOptions = useMemo(() => ([
        { value: 'date', label: 'Date Range' },
        { value: 'month', label: 'Month' },
        { value: 'week', label: 'Week' }
    ]), []);

    useEffect(() => {
        const now = new Date();
        const pad = (val) => String(val).padStart(2, '0');
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(`${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`);
        setEndDate(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
        setMonthValue(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
    }, []);

    useEffect(() => {
        const fetchWeeks = async () => {
            try {
                const response = await axios.get(`${rootUrl}/system/valid_weeks?past=24`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data?.success) {
                    const options = (response.data.valid_weeks || []).map((week) => ({
                        value: week,
                        label: week
                    }));
                    setWeekOptions(options);

                    if (response.data.system_week) {
                        const current = options.find((opt) => opt.value === response.data.system_week) || null;
                        setSelectedWeek(current);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch weeks', err);
            }
        };

        if (token) {
            fetchWeeks();
        }
    }, [token]);

    const runReport = async () => {
        setLoading(true);
        setError('');

        if (filterType === 'date' && (!startDate || !endDate)) {
            setLoading(false);
            setError('Start date and end date are required.');
            return;
        }

        if (filterType === 'month' && !monthValue) {
            setLoading(false);
            setError('Select a month to continue.');
            return;
        }

        if (filterType === 'week' && !selectedWeek?.value) {
            setLoading(false);
            setError('Select a week to continue.');
            return;
        }

        try {
            const params = {};
            if (filterType === 'date') {
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            }

            if (filterType === 'month' && monthValue) {
                params.month = monthValue;
            }

            if (filterType === 'week' && selectedWeek?.value) {
                params.weekKey = selectedWeek.value;
            }

            const response = await axios.get(`${rootUrl}/api/gl/trial-balance`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            if (response.data?.success) {
                setRows(response.data.data || []);
                setTotals(response.data.totals || { total_debit: 0, total_credit: 0 });
                setLastRun(new Date());
            } else {
                setRows([]);
                setTotals({ total_debit: 0, total_credit: 0 });
                setError(response.data?.message || 'Failed to load trial balance');
            }
        } catch (err) {
            setRows([]);
            setTotals({ total_debit: 0, total_credit: 0 });
            setError(err.response?.data?.message || 'Failed to load trial balance');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        const amount = parseFloat(value || 0);
        return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatShortDate = (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const balanceForRow = (row) => {
        const debit = parseFloat(row.total_debit || 0);
        const credit = parseFloat(row.total_credit || 0);
        return row.normal_balance === 'CREDIT' ? credit - debit : debit - credit;
    };

    const totalDifference = parseFloat(totals.total_debit || 0) - parseFloat(totals.total_credit || 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Trial Balance</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Review GL balances by account with date, month, or week filters
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Filter Type</label>
                            <Select
                                options={filterOptions}
                                value={filterOptions.find((opt) => opt.value === filterType)}
                                onChange={(option) => setFilterType(option?.value || 'date')}
                                isSearchable
                            />
                        </div>

                        {filterType === 'date' && (
                            <>
                                <div>
                                    <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-[#2a2e30] mb-2">End Date</label>
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        {filterType === 'month' && (
                            <div>
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Month</label>
                                <input
                                    type="month"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                                    value={monthValue}
                                    onChange={(e) => setMonthValue(e.target.value)}
                                />
                            </div>
                        )}

                        {filterType === 'week' && (
                            <div>
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Week</label>
                                <Select
                                    options={weekOptions}
                                    value={selectedWeek}
                                    onChange={(option) => setSelectedWeek(option)}
                                    isSearchable
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="text-sm text-[#2a2e30]">
                            {lastRun && (
                                <span>
                                    Last run: {formatShortDate(lastRun)}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={runReport}
                            disabled={loading}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 py-2 shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Loading...' : 'Run Report'}
                        </button>
                    </div>

                    {error && (
                        <div className="mt-4 text-sm text-red-500">{error}</div>
                    )}
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Code</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Account</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Normal</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-[#2a2e30] uppercase">Debit</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-[#2a2e30] uppercase">Credit</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-[#2a2e30] uppercase">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-500">
                                            No trial balance data to display.
                                        </td>
                                    </tr>
                                )}
                                {rows.map((row) => (
                                    <tr key={row.account_code} className="border-b border-[#e0e0e0] hover:bg-[#f9f9f9]">
                                        <td className="px-4 py-3 text-sm font-semibold text-[#2a2e30]">{row.account_code}</td>
                                        <td className="px-4 py-3 text-sm text-[#2a2e30]">{row.account_name}</td>
                                        <td className="px-4 py-3 text-sm text-[#2a2e30]">{row.account_type}</td>
                                        <td className="px-4 py-3 text-sm text-[#2a2e30]">{row.normal_balance}</td>
                                        <td className="px-4 py-3 text-sm text-right text-[#2a2e30]">{formatCurrency(row.total_debit)}</td>
                                        <td className="px-4 py-3 text-sm text-right text-[#2a2e30]">{formatCurrency(row.total_credit)}</td>
                                        <td className="px-4 py-3 text-sm text-right text-[#2a2e30]">{formatCurrency(balanceForRow(row))}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-[#f5f5f5]">
                                    <td className="px-4 py-3 text-sm font-semibold text-[#2a2e30]" colSpan="4">Totals</td>
                                    <td className="px-4 py-3 text-sm font-semibold text-right text-[#2a2e30]">{formatCurrency(totals.total_debit)}</td>
                                    <td className="px-4 py-3 text-sm font-semibold text-right text-[#2a2e30]">{formatCurrency(totals.total_credit)}</td>
                                    <td className="px-4 py-3 text-sm font-semibold text-right text-[#2a2e30]">{formatCurrency(totalDifference)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrialBalance;
